<template>
  <div class="timing-window-table">
    <div class="table-header" v-if="title">
      <h3 class="table-title">{{ title }}</h3>
    </div>
    <div class="table-content">
      <table v-if="windows && windows.length">
        <thead>
          <tr>
            <th>Type</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th v-if="hasScore">Score</th>
            <th v-if="hasExpected">Expected</th>
            <th v-if="hasWinRate">Win Rate</th>
            <th>Signal</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(window, index) in windows" :key="index" :class="['window-row', window.type.toLowerCase()]">
            <td>
              <span :class="['type-badge', window.type.toLowerCase()]">
                {{ window.type }}
              </span>
            </td>
            <td class="date">{{ formatDate(window.startDate) }}</td>
            <td class="date">{{ formatDate(window.endDate) }}</td>
            <td v-if="hasScore" class="score">{{ window.score || '-' }}</td>
            <td v-if="hasExpected" class="expected">{{ window.expected || '-' }}</td>
            <td v-if="hasWinRate" class="winrate">
              <span v-if="window.winRate" :class="['winrate-badge', winRateClass(window.winRate)]">{{ window.winRate }}</span>
              <span v-else>-</span>
            </td>
            <td class="signal">
              <span v-if="window.signal">{{ window.signal }}</span>
              <span v-else-if="window.strategies">{{ window.strategies.join(', ') }}</span>
              <span v-else-if="window.reasons">{{ window.reasons.join(', ') }}</span>
              <span v-else>-</span>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="no-data">
        <span class="no-data-icon">&#128197;</span>
        <p>No timing windows available</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  title: {
    type: String,
    default: 'Timing Windows',
  },
  windows: {
    type: Array,
    default: () => [],
  },
});

const hasScore = computed(() => props.windows.some(w => w.score));
const hasExpected = computed(() => props.windows.some(w => w.expected));
const hasWinRate = computed(() => props.windows.some(w => w.winRate));

const winRateClass = (rate) => {
  const num = parseFloat(rate);
  if (isNaN(num)) return '';
  if (num >= 78) return 'high';
  if (num >= 68) return 'good';
  return 'moderate';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};
</script>

<style scoped>
.timing-window-table {
  background: rgba(0, 0, 0, 0.02);
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: visible;
  backdrop-filter: none;
  box-shadow: none;
}

.table-header {
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
}

.table-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.table-content {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

table {
  width: 100%;
  min-width: 520px;
  border-collapse: collapse;
}

thead th {
  text-align: left;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

tbody td {
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.window-row:hover {
  background: var(--bg-tertiary);
}

.window-row.buy {
  border-left: 3px solid var(--success);
}

.window-row.avoid,
.window-row.sell {
  border-left: 3px solid var(--danger);
}

.window-row.watch {
  border-left: 3px solid var(--warning);
}

.type-badge {
  display: inline-block;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.type-badge.buy {
  background: rgba(34, 197, 94, 0.2);
  color: var(--success);
}

.type-badge.avoid,
.type-badge.sell {
  background: rgba(239, 68, 68, 0.2);
  color: var(--danger);
}

.type-badge.watch {
  background: rgba(234, 179, 8, 0.2);
  color: var(--warning);
}

.date {
  font-family: var(--font-mono);
  white-space: nowrap;
}

.score {
  font-family: var(--font-mono);
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
}

.expected {
  font-family: var(--font-mono);
  font-weight: 600;
  color: var(--success);
  white-space: nowrap;
}

.winrate {
  white-space: nowrap;
}

.winrate-badge {
  display: inline-block;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 700;
  font-family: var(--font-mono);
}

.winrate-badge.high {
  background: rgba(34, 197, 94, 0.18);
  color: var(--success);
}

.winrate-badge.good {
  background: rgba(34, 197, 94, 0.10);
  color: #16a34a;
}

.winrate-badge.moderate {
  background: rgba(234, 179, 8, 0.15);
  color: var(--warning);
}

.signal {
  line-height: 1.4;
}

/* Mobile */
@media (max-width: 640px) {
  .timing-window-table {
    border-radius: 12px;
  }

  thead th {
    padding: 0.65rem 0.75rem;
    font-size: 0.7rem;
    letter-spacing: 0.3px;
  }

  tbody td {
    padding: 0.65rem 0.75rem;
    font-size: 0.8rem;
  }

  .date {
    white-space: normal;
    font-size: 0.78rem;
  }

  .signal {
    font-size: 0.78rem;
    line-height: 1.35;
  }

  .score, .expected {
    font-size: 0.78rem;
  }

  .winrate-badge {
    font-size: 0.65rem;
    padding: 0.15rem 0.4rem;
  }

  .type-badge {
    font-size: 0.65rem;
    padding: 0.15rem 0.4rem;
  }

  .table-header {
    padding: 0.75rem;
  }

  .table-title {
    font-size: 0.9rem;
  }
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xl);
  color: var(--text-muted);
}

.no-data-icon {
  font-size: 3rem;
  margin-bottom: var(--spacing-md);
  opacity: 0.5;
}

.no-data p {
  font-size: 0.95rem;
}
</style>
